import React, { useState, useCallback, useMemo } from 'react';

type RatingProps = {
  rating?: number;
  size?: number;
  disabled?: boolean;
  onChange?: (rating: number) => void;
};

export const Rating: React.FC<RatingProps> = ({ rating, size = 14, disabled = false, onChange }) => {
  const [starOnHover, setStarOnHover] = useState<number | null>(null);
  const [localRating, setLocalRating] = useState(rating ?? 0);

  const handleSelectStar = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent, value: number) => {
      event.stopPropagation();
      if (!disabled) {
        onChange(value);
        setLocalRating(value);
      }
    },
    [onChange, disabled]
  );

  const handleMouseEnter = useCallback(
    (value: number) => {
      if (!disabled) setStarOnHover(value);
    },
    [disabled]
  );

  const handleMouseLeave = useCallback(
    (value: number) => {
      if (starOnHover === value) {
        setStarOnHover(null);
      }
    },
    [starOnHover]
  );

  // useMemo prevents array recreation on every render
  const stars = useMemo(() => [1, 2, 3, 4, 5], []);

  const isStarActive = useCallback((i: number) => i <= localRating, [localRating]);

  const isStarHighlighted = useCallback(
    (i: number) => {
      return starOnHover !== null && i <= starOnHover;
    },
    [starOnHover]
  );

  return (
    <div className="rating-component" role="radiogroup" aria-disabled={disabled}>
      {stars.map((i) => (
        <div
          key={i}
          className={[
            'rating-component-star',
            isStarActive(i) ? 'rating-component-star-active' : '',
            isStarHighlighted(i) ? 'rating-component-star-highlighted' : ''
          ].join(' ')}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`Select ${i} stars`}
          onClick={(e) => handleSelectStar(e, i)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleSelectStar(e, i);
          }}
          onMouseEnter={() => handleMouseEnter(i)}
          onMouseLeave={() => handleMouseLeave(i)}
          onFocus={() => handleMouseEnter(i)}
          onBlur={() => handleMouseLeave(i)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" height={size} width={size} viewBox="0 0 24 24" aria-hidden="true">
            <path d="m12 17 5 3-1-6 4-4h-6l-2-6-2 6H4l4 4-1 6 5-3Z" />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default React.memo(Rating);
